import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { saveCreation } from 'Vendor/Store/studio/thunk';
import { clearHistory } from 'Vendor/Store/undoable';

interface Props {
    creationId?: number;
    debounceMs?: number;
    enabled?: boolean;
}

const useAutoSaveCreation = ({
    creationId,
    debounceMs = 2500,
    enabled = true,
}: Props) => {
    const dispatch = useDispatch<AppDispatch>();

    const pastLength = useSelector((s: RootState) => s.studio.past.length);

    const pastRef = useRef(pastLength);
    const timerRef = useRef<number | null>(null);
    const savePromiseRef = useRef<Promise<void> | null>(null);
    const resaveRef = useRef(false);

    const initId = useRef<number | null>(null);
    const initPast = useRef(0);

    const [hasPendingSave, setHasPendingSave] = useState(false);

    useEffect(() => {
        pastRef.current = pastLength;
    }, [pastLength]);

    useEffect(() => {
        if (!creationId) return;

        if (initId.current !== creationId) {
            initId.current = creationId;
            initPast.current = pastRef.current;
        }
    }, [creationId]);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const hasChanges = useCallback(() => {
        const base = initId.current === creationId ? initPast.current : 0;
        return pastRef.current - base > 0;
    }, [creationId]);

    const runSave = useCallback(async () => {
        if (!creationId || !hasChanges()) {
            setHasPendingSave(false);
            return;
        }

        if (savePromiseRef.current) {
            resaveRef.current = true;
            return savePromiseRef.current;
        }

        const task = (async () => {
            do {
                resaveRef.current = false;
                await dispatch(saveCreation()).unwrap();
            } while (resaveRef.current);

            dispatch(clearHistory());
        })();

        savePromiseRef.current = task;

        try {
            await task;
        } finally {
            savePromiseRef.current = null;
            setHasPendingSave(hasChanges());
        }
    }, [dispatch, creationId, hasChanges]);

    const scheduleSave = useCallback(() => {
        if (!enabled || !hasChanges()) {
            setHasPendingSave(false);
            return;
        }

        clearTimer();

        timerRef.current = window.setTimeout(() => {
            runSave().catch(() => {});
            timerRef.current = null;
        }, debounceMs);

        setHasPendingSave(true);
    }, [enabled, debounceMs, runSave, clearTimer, hasChanges]);

    useEffect(() => {
        scheduleSave();
        return clearTimer;
    }, [pastLength, creationId, scheduleSave, clearTimer]);

    useEffect(() => {
        const flush = () => {
            clearTimer();
            runSave().catch(() => {});
        };

        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') flush();
        });

        return () => {
            window.removeEventListener('pagehide', flush);
        };
    }, [runSave, clearTimer]);

    const forceSave = useCallback(async () => {
        clearTimer();
        await runSave();
    }, [runSave, clearTimer]);

    return { forceSave, hasPendingSave };
}

export default useAutoSaveCreation;
